import React from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles } from "lucide-react";
import { SiSpotify, SiApplemusic, SiYoutubemusic, SiTiktok, SiInstagram } from "react-icons/si";
import { Disc3, Music } from "lucide-react";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/6657fd82-3e2f-4bde-9317-9782f5512de1/images/18fd98e1c39f342691c2171987c3b625b8fd4ddb9279242c6cc4a082c344f6d2.png";

const FLOATING = [
  { Icon: SiSpotify, color: "#1DB954", name: "Spotify", top: "8%", right: "-2%", delay: 0 },
  { Icon: SiApplemusic, color: "#FA243C", name: "Apple Music", top: "62%", right: "-4%", delay: 1.2 },
  { Icon: SiYoutubemusic, color: "#FF0000", name: "YT Music", top: "20%", left: "-2%", delay: 0.6 },
  { Icon: SiTiktok, color: "#ffffff", name: "TikTok", top: "70%", left: "-3%", delay: 1.8 },
  { Icon: SiInstagram, color: "#E4405F", name: "Instagram", top: "45%", left: "8%", delay: 2.4 },
  { Icon: Disc3, color: "#a238ff", name: "Deezer", top: "35%", right: "6%", delay: 0.9 },
  { Icon: Music, color: "#00A8E1", name: "Amazon", top: "85%", right: "30%", delay: 1.5 },
];

export default function Hero() {
  const scroll = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28">
      <div className="absolute inset-0 grid-pattern opacity-40 [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-right">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs text-slate-200"
              data-testid="hero-badge"
            >
              <Sparkles className="w-3.5 h-3.5 text-purple-300" />
              انتشار بین‌المللی برای هنرمندان ایرانی
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.15] tracking-tight"
              data-testid="hero-headline"
            >
              انتشار <span className="gradient-text-neon">جهانی</span> موسیقی؛
              <br />
              بدون <span className="gradient-text">مرز</span>، بدون دردسر.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 text-base md:text-lg text-slate-300 leading-relaxed max-w-2xl"
            >
              پخش رسمی آهنگ و موزیک‌ویدیو در پلتفرم‌های بین‌المللی و نقد کردن درآمد ارزی شما در ایران.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scroll("#register")}
                className="btn-primary"
              >
                شروع انتشار موزیک
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scroll("#pricing")}
                className="btn-secondary"
              >
                مشاهده تعرفه‌ها
              </button>
            </motion.div>
          </div>

          {/* Right Visual */}
          <div className="lg:col-span-5 relative h-[440px] md:h-[540px]">
            <div className="absolute inset-0 rounded-[2rem] overflow-hidden glass">
              <img
                src={HERO_IMG}
                alt="3D Music Visual"
                className="w-full h-full object-cover opacity-90"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#05050A] via-transparent to-transparent" />
            </div>

            {/* Floating platform chips */}
            {FLOATING.map(({ Icon, color, name, top, right, left, delay }, i) => (
              <motion.div
                key={i}
                style={{ top, right, left }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 + i * 0.08 }}
                className="absolute"
              >
                <div
                  className="float-anim glass rounded-2xl px-3 py-2 flex items-center gap-2 shadow-xl"
                  style={{ animationDelay: `${delay}s` }}
                >
                  <Icon style={{ color }} className="w-5 h-5" />
                  <span className="text-xs text-slate-200 font-medium">{name}</span>
                </div>
              </motion.div>
            ))}

            {/* Dashboard chip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="absolute -bottom-4 right-6 glass rounded-2xl p-4 w-56 shadow-2xl"
            >
              <div className="text-xs text-slate-400 mb-1">درآمد تخمینی شما</div>
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-2xl font-extrabold gradient-text-neon">۱۲,۴۸۰$</div>
                  <div className="text-[10px] text-slate-400">درآمد ماه جاری</div>
                </div>
                <div className="flex items-end gap-0.5 h-10">
                  {[8, 14, 10, 18, 22, 16, 26].map((h, i) => (
                    <div
                      key={i}
                      className="w-1.5 rounded-sm bg-gradient-to-t from-purple-500 to-cyan-300"
                      style={{ height: `${h * 1.5}px` }}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
